/**
 * auth.ts
 *
 * Attaches the bearer token and handles token refresh on 401.
 */
import { Code, ConnectError, type Interceptor } from "@connectrpc/connect";

/**
 * The slice of the auth store this interceptor needs.
 *
 * Declared structurally rather than imported so that this module never
 * depends on Pinia or the router. Importing `useAuthStore` here is what
 * previously made every store import boot the router.
 */
export interface AuthStoreLike {
  getAccessToken(): string | null;
  refreshAccessToken(): Promise<boolean>;
}

/** Dependencies of the auth interceptor. */
export interface AuthInterceptorDeps {
  /** Resolves the auth store, per request rather than at construction. */
  getAuthStore: () => AuthStoreLike;
}

/**
 * Builds the authentication interceptor.
 *
 * Sets the Authorization header from the current access token. On an
 * Unauthenticated response it attempts a token refresh and replays the
 * request once. If the refresh fails, the auth store has already decided
 * whether to end the session, so this only throws to unwind the caller's
 * loading state.
 *
 * @param deps - Accessor for the auth store.
 * @returns A Connect interceptor.
 */
export function createAuthInterceptor(deps: AuthInterceptorDeps): Interceptor {
  return (next) => async (req) => {
    const authStore = deps.getAuthStore();
    // Always read from authStore getter (which reads from localStorage)
    const token = authStore.getAccessToken();

    if (token) {
      req.header.set("Authorization", `Bearer ${token}`);
    }

    try {
      return await next(req);
    } catch (error) {
      // Handle authentication errors (token expired/invalid)
      if (error instanceof ConnectError && error.code === Code.Unauthenticated) {
        const success = await authStore.refreshAccessToken();

        if (success) {
          // Read fresh token from localStorage via getter
          const newToken = authStore.getAccessToken();
          if (newToken) {
            req.header.set("Authorization", `Bearer ${newToken}`);
          }
          return await next(req);
        }

        // The refresh failed. For a terminal failure the auth store has
        // already cleared the tokens, warned the user and pushed /login; for a
        // transient outage it kept the session so a later request can retry.
        // Either way this request is done: throw to unwind the caller's
        // loading state rather than leaving it spinning.
        throw new ConnectError(
          "Phiên đăng nhập đã hết hạn",
          Code.Unauthenticated,
        );
      }

      throw error;
    }
  };
}
